import { useState } from 'react';
import { AlertDialog, Button, Callout, PropertyList, PropertyListItem, RadioButton, RadioGroup, SelectField, Switch, TextArea, TextField, WizardModal } from '@humanforce/mango-ui-react';
import type {
  MandatoryContentDescriptorDto,
  PayAdviceBackPayTreatment,
  PayAdviceDeliveryMethod,
  PayAdviceFdvlDisplayMode,
  PayAdviceLeaveProjectionMode,
  PayAdviceLogoPosition,
  PayAdviceLogoSize,
  PayAdvicePayTypeAggregation,
  PayAdviceTemplateDetailsDto,
  SavePayAdviceTemplateRequestDto,
} from '../organisationApiTypes';
import { advisoryTone } from '../payAdviceAdvisoryTone';

interface PayAdviceTemplateWizardProps {
  visible: boolean;
  mode: 'create' | 'edit';
  template: PayAdviceTemplateDetailsDto | null;
  mandatoryContent: MandatoryContentDescriptorDto[];
  // True when the server's own canEdit capability flag is false (an archived
  // template, or one under an inactive Business Record) — the template stays
  // openable to view per plan section 10.2, but every field is disabled and
  // there is no save action, rather than the server refusing on submit.
  readOnly: boolean;
  saving: boolean;
  saveError: string | null;
  onSave: (request: SavePayAdviceTemplateRequestDto) => void;
  onClose: () => void;
}

// Draft shape mirrors SavePayAdviceTemplateRequestDto, but fdvlDisplayMode
// starts unselected (D-17: "nothing is preselected", so the statutory gate is
// visible rather than pre-satisfied) — a request-shaped null the DTO itself
// permits, so no separate draft type is needed for that one field.
type Draft = SavePayAdviceTemplateRequestDto;

const FDVL_FREE_TEXT_MAX = 50;

const emptyDraft = (): Draft => ({
  name: '',
  description: '',
  logoImageId: '',
  logoPosition: 'header_left',
  logoSize: 'medium',
  showYearToDate: true,
  showOrganisationUnit: true,
  showPayrollLabel: true,
  showAwardClassification: true,
  showSuperannuationDetail: true,
  showBankAccountMasking: true,
  showQrDeepLink: true,
  showPayrollContact: true,
  showPlainEnglishTaxScale: true,
  backPayTreatment: 'detailed',
  payTypeAggregation: 'roll_up',
  leaveProjectionMode: 'anniversary',
  deliveryMethod: 'electronic',
  emailEnabled: true,
  portalEnabled: true,
  printSupplierReference: '',
  fdvlDisplayMode: null,
  fdvlDisplayPayTypeId: '',
  fdvlDisplayFreeText: '',
});

const draftFromTemplate = (template: PayAdviceTemplateDetailsDto): Draft => ({
  name: template.name,
  description: template.description ?? '',
  logoImageId: template.logoImageId ?? '',
  logoPosition: template.logoPosition,
  logoSize: template.logoSize,
  showYearToDate: template.showYearToDate,
  showOrganisationUnit: template.showOrganisationUnit,
  showPayrollLabel: template.showPayrollLabel,
  showAwardClassification: template.showAwardClassification,
  showSuperannuationDetail: template.showSuperannuationDetail,
  showBankAccountMasking: template.showBankAccountMasking,
  showQrDeepLink: template.showQrDeepLink,
  showPayrollContact: template.showPayrollContact,
  showPlainEnglishTaxScale: template.showPlainEnglishTaxScale,
  backPayTreatment: template.backPayTreatment,
  payTypeAggregation: template.payTypeAggregation,
  leaveProjectionMode: template.leaveProjectionMode,
  deliveryMethod: template.deliveryMethod,
  emailEnabled: template.emailEnabled,
  portalEnabled: template.portalEnabled,
  printSupplierReference: template.printSupplierReference ?? '',
  fdvlDisplayMode: template.fdvlDisplayMode,
  fdvlDisplayPayTypeId: template.fdvlDisplayPayTypeId ?? '',
  fdvlDisplayFreeText: template.fdvlDisplayFreeText ?? '',
});

const LOGO_POSITION_OPTIONS: { label: string; value: PayAdviceLogoPosition }[] = [
  { label: 'Header, left', value: 'header_left' },
  { label: 'Header, centre', value: 'header_centre' },
  { label: 'Header, right', value: 'header_right' },
];

const LOGO_SIZE_OPTIONS: { label: string; value: PayAdviceLogoSize }[] = [
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large', value: 'large' },
];

const BACK_PAY_OPTIONS: { label: string; value: PayAdviceBackPayTreatment; description: string }[] = [
  { label: 'Detailed', value: 'detailed', description: 'Back pay is itemised in full on the same Pay Advice.' },
  { label: 'Summary', value: 'summary', description: 'Back pay appears as a single summarised line.' },
  { label: 'Off', value: 'off', description: 'Back pay is presented on a separate, second Pay Advice document sharing the same pay date.' },
];

const AGGREGATION_OPTIONS: { label: string; value: PayAdvicePayTypeAggregation; description: string }[] = [
  { label: 'Roll up', value: 'roll_up', description: 'One line per Pay Type for the whole period. Example: 38 ordinary hours shown as a single line.' },
  { label: 'Date by date', value: 'date_by_date', description: 'One line per Pay Type per date worked. Example: eight separate lines for an eight-day fortnight.' },
  { label: 'Date by date with summary', value: 'date_by_date_with_summary', description: 'Both: a per-date breakdown plus a summary line whose totals must match it.' },
];

const LEAVE_PROJECTION_OPTIONS: { label: string; value: PayAdviceLeaveProjectionMode }[] = [
  { label: 'Off', value: 'off' },
  { label: 'To end of financial year', value: 'eofy' },
  { label: 'To work anniversary', value: 'anniversary' },
];

const CONTENT_TOGGLES: { key: keyof Pick<Draft,
  'showYearToDate' | 'showOrganisationUnit' | 'showPayrollLabel' | 'showAwardClassification' |
  'showSuperannuationDetail' | 'showBankAccountMasking' | 'showQrDeepLink' | 'showPayrollContact' | 'showPlainEnglishTaxScale'
>; label: string }[] = [
  { key: 'showYearToDate', label: 'Year-to-date totals' },
  { key: 'showOrganisationUnit', label: 'Organisation unit' },
  { key: 'showPayrollLabel', label: 'Payroll label' },
  { key: 'showAwardClassification', label: 'Award / classification' },
  { key: 'showSuperannuationDetail', label: 'Superannuation detail' },
  { key: 'showBankAccountMasking', label: 'Bank account masking' },
  { key: 'showQrDeepLink', label: 'QR deep link' },
  { key: 'showPayrollContact', label: 'Payroll contact' },
  { key: 'showPlainEnglishTaxScale', label: 'Plain-English tax scale' },
];

const includesPrint = (method: PayAdviceDeliveryMethod) => method === 'print' || method === 'both';
const includesElectronic = (method: PayAdviceDeliveryMethod) => method === 'electronic' || method === 'both';

const STEP_LABELS = ['Identity and branding', 'Content', 'Presentation', 'Delivery', 'FDVL display', 'Review'];

// D-17: Content, Presentation and Delivery are three enum choices about how the
// same figures are presented, so they read as one step rather than one each;
// FDVL keeps its own step because it is the statutory field.
export const PayAdviceTemplateWizard = ({ visible, mode, template, mandatoryContent, readOnly, saving, saveError, onSave, onClose }: PayAdviceTemplateWizardProps) => {
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<Draft>(() => (template ? draftFromTemplate(template) : emptyDraft()));
  const [touched, setTouched] = useState<Set<number>>(new Set());
  const [discardConfirm, setDiscardConfirm] = useState(false);

  // Re-seeds the draft each time the wizard opens fresh — Modal/WizardModal
  // stay mounted between opens in this codebase's pattern (see PayGroupsPanel),
  // so state has to be reset on the triggering prop rather than in an effect.
  const key = `${visible}-${template?.payAdviceTemplateId ?? 'new'}-${template?.payAdviceTemplateVersionId ?? ''}`;
  const [lastKey, setLastKey] = useState(key);
  if (key !== lastKey) {
    setLastKey(key);
    setStep(1);
    setDraft(template ? draftFromTemplate(template) : emptyDraft());
    setTouched(new Set());
  }

  const isDirty = JSON.stringify(draft) !== JSON.stringify(template ? draftFromTemplate(template) : emptyDraft());

  const set = <K extends keyof Draft>(field: K, value: Draft[K]) => setDraft((current) => ({ ...current, [field]: value }));
  const touch = (current: number) => setTouched((set_) => new Set(set_).add(current));

  const requestClose = () => {
    // D-18: a part-filled wizard is the genuinely lossy path, so closing it
    // confirms through the destructive severity; a fresh, untouched wizard
    // (nothing typed yet) closes immediately, since there is nothing to lose.
    // A read-only view can never become dirty, but the check is explicit
    // rather than relied upon, since nothing there can be lost either way.
    if (isDirty && !readOnly) { setDiscardConfirm(true); return; }
    onClose();
  };
  const confirmDiscard = () => { setDiscardConfirm(false); onClose(); };

  // Step 1: identity and branding.
  const step1Invalid = !draft.name.trim();

  // Step 4: delivery. BR-PA-015 — at least one channel that would actually deliver.
  const deliveryInvalid = !includesPrint(draft.deliveryMethod) && !draft.emailEnabled && !draft.portalEnabled;

  // Step 5: FDVL. Mandatory (BR-PA-017); exactly one of the two values for the
  // chosen mode (BR-PA-018/019/020) — mirrors the server's own rule so the
  // statutory gate is enforced before a request is even sent.
  const fdvlFreeText = draft.fdvlDisplayFreeText?.trim() ?? '';
  const fdvlPayTypeId = draft.fdvlDisplayPayTypeId?.trim() ?? '';
  const fdvlInvalid =
    !draft.fdvlDisplayMode ||
    (draft.fdvlDisplayMode === 'pay_type' && !fdvlPayTypeId) ||
    (draft.fdvlDisplayMode === 'free_text' && (!fdvlFreeText || fdvlFreeText.length > FDVL_FREE_TEXT_MAX));

  const stepInvalid = [step1Invalid, false, false, deliveryInvalid, fdvlInvalid, false];
  const canAdvance = !stepInvalid[step - 1];

  const goNext = () => { touch(step); if (!canAdvance) return; setStep((s) => Math.min(s + 1, STEP_LABELS.length)); };
  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  const submit = () => {
    touch(5);
    if (step1Invalid || deliveryInvalid || fdvlInvalid) { setStep(fdvlInvalid ? 5 : deliveryInvalid ? 4 : 1); return; }
    onSave({
      ...draft,
      name: draft.name.trim(),
      description: draft.description?.trim() || undefined,
      logoImageId: draft.logoImageId?.trim() || undefined,
      printSupplierReference: includesPrint(draft.deliveryMethod) ? (draft.printSupplierReference?.trim() || undefined) : undefined,
      fdvlDisplayPayTypeId: draft.fdvlDisplayMode === 'pay_type' ? fdvlPayTypeId : undefined,
      fdvlDisplayFreeText: draft.fdvlDisplayMode === 'free_text' ? fdvlFreeText : undefined,
    });
  };

  return (
    <>
      <WizardModal
        visible={visible}
        sidebarTitle={mode === 'edit' ? (readOnly ? 'View template' : 'Edit template') : 'New template'}
        steps={STEP_LABELS.map((label) => ({ label }))}
        currentStep={step}
        header={STEP_LABELS[step - 1]}
        onHide={requestClose}
        onStepChange={setStep}
        footer={
          <>
            {/* "Done", not "Close" — WizardModal's own header already has a
                "Close" icon button (closable defaults true), and a second
                control with the identical accessible name would collide. */}
            <Button label={readOnly ? 'Done' : 'Cancel'} variant="secondary" disabled={saving} onClick={requestClose} />
            {step > 1 && <Button label="Back" variant="secondary" disabled={saving} onClick={goBack} />}
            {step < STEP_LABELS.length && <Button label="Next" disabled={saving} onClick={goNext} />}
            {step === STEP_LABELS.length && !readOnly && (
              <Button label={mode === 'edit' ? 'Save changes' : 'Create template'} loading={saving} disabled={saving} onClick={submit} />
            )}
          </>
        }
      >
        <div className="mgo-stack mgo-gap-l">
          {readOnly && (
            <Callout tone="neutral" title="Read-only">
              This template cannot be edited{template?.status === 'archived' ? ' because it is archived' : ' while its Business Record is inactive'}.
            </Callout>
          )}
          {template?.advisories.map((advisory) => (
            <Callout key={`${advisory.code}-${advisory.targetId ?? ''}`} tone={advisoryTone(advisory.code)}>{advisory.message}</Callout>
          ))}
          {saveError && <Callout tone="critical" title="Save failed">{saveError}</Callout>}

          {step === 1 && (
            <div className="mgo-stack mgo-gap-l">
              <TextField
                label="Name"
                required
                autoFocus
                disabled={readOnly}
                value={draft.name}
                status={touched.has(1) && step1Invalid ? 'critical' : undefined}
                statusText={touched.has(1) && step1Invalid ? 'Name is required.' : undefined}
                onChange={(event) => set('name', event.target.value)}
              />
              <TextArea label="Description" disabled={readOnly} value={draft.description ?? ''} onChange={(event) => set('description', event.target.value)} />
              <Callout tone="info" title="Brand colours are configured elsewhere">
                This template controls the logo only. Brand colours are set on Organisation Details.
              </Callout>
              <TextField
                label="Logo asset reference"
                helpText="Media library is not yet available in this environment. Leave blank to use the Organisation's default logo."
                disabled={readOnly}
                value={draft.logoImageId ?? ''}
                onChange={(event) => set('logoImageId', event.target.value)}
              />
              <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
                <SelectField label="Logo position" disabled={readOnly} value={draft.logoPosition} options={LOGO_POSITION_OPTIONS} searchable={false} onChange={(value) => set('logoPosition', value as PayAdviceLogoPosition)} />
                <SelectField label="Logo size" disabled={readOnly} value={draft.logoSize} options={LOGO_SIZE_OPTIONS} searchable={false} onChange={(value) => set('logoSize', value as PayAdviceLogoSize)} />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="mgo-stack mgo-gap-l">
              <Callout tone="info" title="Always included">
                These items are required by Fair Work Regulation 3.46 and cannot be switched off.
              </Callout>
              <PropertyList>
                {mandatoryContent.map((item) => <PropertyListItem key={item.code} label={item.label}>Always shown</PropertyListItem>)}
              </PropertyList>
              <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
                {CONTENT_TOGGLES.map(({ key, label }) => (
                  <Switch
                    key={key}
                    id={`toggle-${key}`}
                    label={label}
                    disabled={readOnly}
                    checked={draft[key]}
                    onChange={(checked) => set(key, checked)}
                  />
                ))}
              </div>
              {!draft.showBankAccountMasking && (
                <Callout tone="caution" title="Bank account masking is off">
                  Employees&apos; full bank account numbers will be printed on the Pay Advice.
                </Callout>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="mgo-stack mgo-gap-l">
              <RadioGroup label="Back-pay treatment" name="backPayTreatment" disabled={readOnly} value={draft.backPayTreatment} onChange={(value) => set('backPayTreatment', value as PayAdviceBackPayTreatment)}>
                {BACK_PAY_OPTIONS.map((option) => <RadioButton key={option.value} value={option.value} label={option.label} helpText={option.description} />)}
              </RadioGroup>
              <RadioGroup label="Pay-type aggregation" name="payTypeAggregation" disabled={readOnly} value={draft.payTypeAggregation} onChange={(value) => set('payTypeAggregation', value as PayAdvicePayTypeAggregation)}>
                {AGGREGATION_OPTIONS.map((option) => <RadioButton key={option.value} value={option.value} label={option.label} helpText={option.description} />)}
              </RadioGroup>
              <SelectField label="Leave projection" disabled={readOnly} value={draft.leaveProjectionMode} options={LEAVE_PROJECTION_OPTIONS} searchable={false} onChange={(value) => set('leaveProjectionMode', value as PayAdviceLeaveProjectionMode)} />
            </div>
          )}

          {step === 4 && (
            <div className="mgo-stack mgo-gap-l">
              <SelectField
                label="Delivery method"
                disabled={readOnly}
                value={draft.deliveryMethod}
                options={[{ label: 'Electronic', value: 'electronic' }, { label: 'Print', value: 'print' }, { label: 'Both', value: 'both' }]}
                searchable={false}
                onChange={(value) => set('deliveryMethod', value as PayAdviceDeliveryMethod)}
              />
              {includesElectronic(draft.deliveryMethod) && (
                <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
                  <Switch id="delivery-email" label="Email" disabled={readOnly} checked={draft.emailEnabled} onChange={(checked) => set('emailEnabled', checked)} />
                  <Switch id="delivery-portal" label="Employee portal" disabled={readOnly} checked={draft.portalEnabled} onChange={(checked) => set('portalEnabled', checked)} />
                </div>
              )}
              {includesPrint(draft.deliveryMethod) && (
                <TextField label="Print supplier reference" disabled={readOnly} value={draft.printSupplierReference ?? ''} onChange={(event) => set('printSupplierReference', event.target.value)} />
              )}
              {touched.has(4) && deliveryInvalid && (
                <Callout tone="critical" title="At least one delivery channel is required">
                  Enable email or the employee portal, or include print delivery.
                </Callout>
              )}
            </div>
          )}

          {step === 5 && (
            <div className="mgo-stack mgo-gap-l">
              <Callout tone="info" title="Family and domestic violence leave display">
                This setting is mandatory under the Fair Work Amendment Act 2022. It exists so this leave is not
                identifiable to anyone who sees the Pay Advice. Choose a Pay Type label or a discreet free-text label.
              </Callout>
              <RadioGroup
                label="Display mode"
                name="fdvlDisplayMode"
                disabled={readOnly}
                value={draft.fdvlDisplayMode ?? ''}
                status={touched.has(5) && !draft.fdvlDisplayMode ? 'critical' : undefined}
                statusText={touched.has(5) && !draft.fdvlDisplayMode ? 'An FDVL display setting is required.' : undefined}
                onChange={(value) => set('fdvlDisplayMode', value as PayAdviceFdvlDisplayMode)}
              >
                <RadioButton value="pay_type" label="Show as a Pay Type" />
                <RadioButton value="free_text" label="Show as free text" />
              </RadioGroup>
              {draft.fdvlDisplayMode === 'pay_type' && (
                <TextField
                  label="Pay Type reference"
                  helpText="Must resolve to an active Pay Type under this Organisation."
                  disabled={readOnly}
                  value={draft.fdvlDisplayPayTypeId ?? ''}
                  status={touched.has(5) && !fdvlPayTypeId ? 'critical' : undefined}
                  statusText={touched.has(5) && !fdvlPayTypeId ? 'A Pay Type reference is required.' : undefined}
                  onChange={(event) => set('fdvlDisplayPayTypeId', event.target.value)}
                />
              )}
              {draft.fdvlDisplayMode === 'free_text' && (
                <TextArea
                  label="Free-text label"
                  rows={1}
                  resize="none"
                  helpText='Consider a discreet label such as "Normal Hours" — avoid wording that names the leave.'
                  characterLimit={FDVL_FREE_TEXT_MAX}
                  disabled={readOnly}
                  value={draft.fdvlDisplayFreeText ?? ''}
                  status={touched.has(5) && (!fdvlFreeText || fdvlFreeText.length > FDVL_FREE_TEXT_MAX) ? 'critical' : undefined}
                  statusText={touched.has(5) && !fdvlFreeText ? 'Free text is required.' : touched.has(5) && fdvlFreeText.length > FDVL_FREE_TEXT_MAX ? `Cannot exceed ${FDVL_FREE_TEXT_MAX} characters.` : undefined}
                  onChange={(event) => set('fdvlDisplayFreeText', event.target.value)}
                />
              )}
            </div>
          )}

          {step === 6 && (
            <PropertyList>
              <PropertyListItem label="Name">{draft.name || '—'}</PropertyListItem>
              <PropertyListItem label="Logo">{draft.logoImageId || 'Organisation default'}</PropertyListItem>
              <PropertyListItem label="Back-pay treatment">{BACK_PAY_OPTIONS.find((o) => o.value === draft.backPayTreatment)?.label}</PropertyListItem>
              <PropertyListItem label="Pay-type aggregation">{AGGREGATION_OPTIONS.find((o) => o.value === draft.payTypeAggregation)?.label}</PropertyListItem>
              <PropertyListItem label="Leave projection">{LEAVE_PROJECTION_OPTIONS.find((o) => o.value === draft.leaveProjectionMode)?.label}</PropertyListItem>
              <PropertyListItem label="Delivery">{draft.deliveryMethod}{includesElectronic(draft.deliveryMethod) ? ` (email: ${draft.emailEnabled ? 'on' : 'off'}, portal: ${draft.portalEnabled ? 'on' : 'off'})` : ''}</PropertyListItem>
              <PropertyListItem label="FDVL display">{draft.fdvlDisplayMode === 'pay_type' ? `Pay Type: ${fdvlPayTypeId}` : draft.fdvlDisplayMode === 'free_text' ? `Free text: "${fdvlFreeText}"` : 'Not set'}</PropertyListItem>
            </PropertyList>
          )}
        </div>
      </WizardModal>

      <AlertDialog
        visible={discardConfirm}
        severity="critical"
        header="Discard changes?"
        confirmLabel="Discard changes"
        onConfirm={confirmDiscard}
        onCancel={() => setDiscardConfirm(false)}
      >
        This template has not been saved. Closing now discards everything entered in this wizard.
      </AlertDialog>
    </>
  );
};
